import { isValid } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash-es';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { FormProvider, useForm, useFormContext, UseFormReturn } from 'react-hook-form';
import { FormFactoryWithStandardLayout } from '../../../components/Form/FormFactory';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { transactionTypesByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { TRANSACTION_SUB_TYPES } from '../../../types';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { createFormFromFieldsArray, IForm } from '../../../view-models/form.view-model';
import {
  fieldKeyToRestructureKey,
  getPpsPerShareClassDataModel,
  ITransactionViewModel,
  ITransactionViewModelInternal,
  useCreateTransactionViewModel,
  useFromTransactionDataModel,
  useGetShareExchangeDetails,
} from '../../../view-models/transaction-form.view-model';
import { useTransactionResolver } from './TransactionFormStep';
import { useTransactionFields } from './TransactionFormUtils';

export type TransactionFormMethodsRef = {
  methods: UseFormReturn<ITransactionViewModel | Partial<ITransactionViewModel>>;
};

type ITransactionFormProps = Omit<IForm, 'fields'> & {
  companyId: number;
  transactionTypeId: number;
  transactionData?: ITransactionDataModel | null;
};

export const TransactionForm = forwardRef(
  (props: ITransactionFormProps, ref: ForwardedRef<TransactionFormMethodsRef | null>) => {
    const { companyId, transactionTypeId, transactionData } = props;
    const createTransactionDataModel = useCreateTransactionViewModel(companyId);
    const fromTransactionDataModel = useFromTransactionDataModel(companyId);
    const defaultValues = useMemo(() => {
      return transactionData
        ? fromTransactionDataModel(transactionData)
        : createTransactionDataModel(transactionTypeId);
    }, [createTransactionDataModel, fromTransactionDataModel, transactionTypeId, transactionData]);
    const getResolver = useTransactionResolver();

    const methods = useForm({
      defaultValues,
      mode: 'all',
      resolver: getResolver(transactionTypeId),
    });

    useImperativeHandle(ref, () => {
      return { methods };
    });

    return (
      <FormProvider {...methods}>
        <TransactionFormInner companyId={companyId} transactionTypeId={transactionTypeId} />
      </FormProvider>
    );
  }
);
TransactionForm.displayName = 'TransactionForm';

function TransactionFormInner(props: ITransactionFormProps) {
  const { companyId, transactionTypeId, title = '', variant = 'form' } = props;
  const { reset, watch } = useFormContext<ITransactionViewModel>();
  const transactionFields = useTransactionFields(transactionTypeId);
  const transactionTypesById = useAtomValue(transactionTypesByIdMapAtom);
  const getShareExchangeDetails = useGetShareExchangeDetails(companyId);
  const { showStockSplitInEquitySell } = useFlags<MaggieFeatureFlags>();

  const form = createFormFromFieldsArray(transactionFields, {
    title,
    variant,
  });

  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      const updatedViewModel = cloneDeep(values) as ITransactionViewModel;
      const transactionType = transactionTypesById.get(updatedViewModel.transactionTypeId ?? -1);
      const isShareExchange = transactionType?.name === TRANSACTION_SUB_TYPES.SHARE_EXCHANGE;
      const isEquitySell = transactionType?.name === TRANSACTION_SUB_TYPES.SELL;

      if (type !== 'change') {
        return;
      }

      // We switched to a Share Exchange, or updated the date/fund, so we need to update the share exchange details
      if (isShareExchange) {
        // Kept outside the recompute chain below: it has to run whichever branch fires
        if (name?.startsWith(EXCHANGED_SHARES_PREFIX)) {
          const fieldKey = name.slice(EXCHANGED_SHARES_PREFIX.length);
          updatedViewModel._viewModel.userExchangedSharesByRestructureId = {
            ...updatedViewModel._viewModel.userExchangedSharesByRestructureId,
            [fieldKey]: updatedViewModel._viewModel.exchangedSharesByRestructureId?.[fieldKey],
          };
        }

        if (name === '_viewModel.useSplitByRestructureId' && !showStockSplitInEquitySell) {
          // Clear out the data
          updatedViewModel._viewModel.receivedSharesByRestructureId = {};
          updatedViewModel._viewModel.receivedSecurityByRestructureId = {};
          updatedViewModel._viewModel.receivedPpsByRestructureId = {};
        } else if (
          ((name === '_viewModel.useSplitByRestructureId' && showStockSplitInEquitySell) ||
            name === 'transactionDate' ||
            name === 'fundId') &&
          // skips the recompute while the date is being cleared; a partially typed
          // date still gets through, which is what the user-edit record is for
          hasValidTransactionDate(updatedViewModel)
        ) {
          const date = new Date(updatedViewModel.transactionDate!);
          const fundId = updatedViewModel.fundId;
          // Typed values outrank the live map, which a mid-edit date may have emptied
          const exchangedSharesByRestructureId = getPpsPerShareClassDataModel({
            ...((updatedViewModel._viewModel?.exchangedSharesByRestructureId as Record<string, number>) ??
              {}),
            ...(updatedViewModel._viewModel.userExchangedSharesByRestructureId ?? {}),
          });

          // Combine existing assignments with new ones, prioritizing existing ones
          updatedViewModel._viewModel.exchangedSharesByRestructureId = getShareExchangeDetails({
            fundId,
            date,
            overrides: exchangedSharesByRestructureId,
          });

          if (updatedViewModel._viewModel.useSplitByRestructureId) {
            const receivedSharesByRestructureId =
              (updatedViewModel._viewModel?.receivedSharesByRestructureId as Record<string, number>) ?? {};
            const receivedSecurityByRestructureId =
              (updatedViewModel._viewModel?.receivedSecurityByRestructureId as Record<string, string>) ?? {};
            const receivedPpsByRestructureId =
              (updatedViewModel._viewModel?.receivedPpsByRestructureId as Record<string, number>) ?? {};

            // Remove entries that are no longer valid based on the new exchangedSharesByRestructureId
            for (const key of Object.keys(receivedSharesByRestructureId)) {
              if (!updatedViewModel._viewModel.exchangedSharesByRestructureId[key]) {
                delete receivedSharesByRestructureId[key];
                delete receivedSecurityByRestructureId[key];
                delete receivedPpsByRestructureId[key];
              }
            }
          }
        }

        reset(updatedViewModel);
      } else if (isEquitySell) {
        if (name === '_viewModel.useSplitByRestructureId' && !showStockSplitInEquitySell) {
          // We switch stock split off, clear out exchangedSharesByRestructureId
          updatedViewModel._viewModel.exchangedSharesByRestructureId = null;
        } else if (
          (name === '_viewModel.useSplitByRestructureId' && showStockSplitInEquitySell) ||
          name === 'transactionDate' ||
          name === 'fundId'
        ) {
          // We switch stock split on, or updated the date/fund, update exchangedSharesByRestructureId
          const date = new Date(updatedViewModel.transactionDate!);
          const fundId = updatedViewModel.fundId;
          const exchangedSharesByRestructureId = getPpsPerShareClassDataModel(
            (updatedViewModel._viewModel?.exchangedSharesByRestructureId as Record<string, number>) ?? {}
          );
          updatedViewModel._viewModel.exchangedSharesByRestructureId = getShareExchangeDetails({
            fundId,
            date,
            overrides: exchangedSharesByRestructureId,
          });
        }
        // Handle clearing exchanged shares for unselected securities
        if (showStockSplitInEquitySell && name === '_viewModel.selectedSecurityForSharesSold') {
          const showSharesBySecurity = updatedViewModel._viewModel.useSplitByRestructureId;
          const selectedSecurities = updatedViewModel._viewModel
            .selectedSecurityForSharesSold as ITransactionViewModelInternal['selectedSecurityForSharesSold'];

          if (showSharesBySecurity) {
            const selectedSecuritiesAsSet = new Set(selectedSecurities ?? []);
            const exchangedSharesByRestructureId = {
              ...(updatedViewModel._viewModel.exchangedSharesByRestructureId ?? {}),
            };
            for (const key in exchangedSharesByRestructureId) {
              const fieldKey = fieldKeyToRestructureKey(key);
              if (!selectedSecuritiesAsSet.has(fieldKey)) {
                delete exchangedSharesByRestructureId[key];
              }
            }
            updatedViewModel._viewModel.exchangedSharesByRestructureId = exchangedSharesByRestructureId;
          } else {
            updatedViewModel._viewModel.exchangedSharesByRestructureId = {};
          }
        }

        reset(updatedViewModel);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, reset, transactionTypesById, getShareExchangeDetails, showStockSplitInEquitySell]);

  return <FormFactoryWithStandardLayout form={form} />;
}

const EXCHANGED_SHARES_PREFIX = '_viewModel.exchangedSharesByRestructureId.';

// The date field publishes one valid Date per year keystroke, so 2026 arrives as
// 0002, 0020, 0202, 2026. MUI's own floor is 1900, and any intermediate is <= 999.
const MIN_TRANSACTION_YEAR = 1900;

// new Date(null) is the epoch, so a truthiness check must precede isValid
function hasValidTransactionDate({ transactionDate }: ITransactionViewModel) {
  if (!transactionDate) {
    return false;
  }
  const date = new Date(transactionDate);
  return isValid(date) && date.getFullYear() >= MIN_TRANSACTION_YEAR;
}
